import React, { useEffect, useRef, useState } from "react";
import { BriefcaseIcon, ShieldCheckIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
  where,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import ProfileModalProm from "./ProfileModalProm";
import NotificationHistoryProm from "./NotificationHistoryProm";
import ManagePromotionalPosts from "./ManagePromotionalPosts/ManagePromotionalPosts";
import EditarPerfilesPromocionales from "./EditPromocionalProfiles";
import RenovarMembresia from "./RenewMembership";
import PromotionalProfileModal from "./PromotionalProfileModal";
import AutoridadesSistema from "./MultiHubView/views/AutoridadesSistema";
import AcercaDeSit from "./MultiHubView/views/AcercaDeSit";
import Swal from "sweetalert2";
import AlertModal from "./AlertModal";
  

/**
 * PromotionalHome
 * Props:
 *  - currentUser: objeto del usuario (uid/email/displayName)
 */
export default function PromotionalHome({ currentUser }) {
  const { logout } = useAuth();
  const prevStatusRef = useRef(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [hasPromoProfiles, setHasPromoProfiles] = useState(false);
  const [promoProfiles, setPromoProfiles] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAutoridades, setShowAutoridades] = useState(false);
  const [showAcercaDe, setShowAcercaDe] = useState(false);

  // Modales / pantallas internas
  const [showManagePromotionalPosts, setShowManagePromotionalPosts] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showPromotionalModal, setShowPromotionalModal] = useState(false);

  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  // ── Notificación cuando la cuenta pasa de pendiente a verificada ──────────
  useEffect(() => {
    const currentStatus = currentUser?.status;

    if (prevStatusRef.current === null) {
      prevStatusRef.current = currentStatus;
      return;
    }

    if (prevStatusRef.current === "pending" && currentStatus === "verified") {
      Swal.fire({
        icon: "success",
        title: "¡Cuenta verificada!",
        text: "Tu cuenta fue aprobada. Ya tienes acceso completo a SIT.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 6000,
        timerProgressBar: true,
      });
    }

    prevStatusRef.current = currentStatus;
  }, [currentUser?.status]);

  // Función para cerrar alerta
  const handleCloseAlert = () => {
    if (!selectedAlert?.id) return;
  
    const closedIds = JSON.parse(localStorage.getItem("closedAlertIds") || "[]");
    if (!closedIds.includes(selectedAlert.id)) {
      closedIds.push(selectedAlert.id);
      localStorage.setItem("closedAlertIds", JSON.stringify(closedIds));
    }
  
    setSelectedAlert(null);
  };
  
  //Alertas del sistema
  useEffect(() => {
  const q = query(
    collection(db, "alertas"),
    where("activo", "==", true),
    where("audiencia", "in", ["promocional"]), // Filtra por audiencia
    orderBy("fechaCreacion", "desc")
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setAlerts(data);
  });

  return () => unsubscribe();
}, []);
  
  useEffect(() => {
  if (alerts.length === 0) return;

  const closedIds = JSON.parse(localStorage.getItem("closedAlertIds") || "[]");

  const activeAlert = alerts.find(
    (a) => !closedIds.includes(a.id)
  );

  if (activeAlert) {
    setSelectedAlert(activeAlert);
  } else {
    setSelectedAlert(null);
  }
}, [alerts]);


  useEffect(() => {
    fetchPromoProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const fetchPromoProfiles = async () => {
    if (!currentUser?.uid) return;
    setLoadingProfiles(true);
    try {
      const q = query(
        collection(db, "users", currentUser.uid, "promoProfiles"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPromoProfiles(arr);
      setHasPromoProfiles(arr.length > 0);
    } catch (err) {
      console.error("Error fetching promo profiles:", err);
      setHasPromoProfiles(false);
      Swal.fire("Error", "No se pudieron cargar los perfiles promocionales.", "error");
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleCreatePromoProfile = async (formData) => {
    if (!currentUser?.uid) return;
    try {
      await addDoc(
        collection(db, "users", currentUser.uid, "promoProfiles"),
        {
          name: formData.name,
          description: formData.description,
          tags: formData.tags.split(",").map((t) => t.trim()).filter(Boolean),
          profilePhoto: formData.profilePhoto ? formData.profilePhoto : null,
          externalLinks: formData.externalLinks ? formData.externalLinks.split(",").map((l) => l.trim()).filter(Boolean) : [],
          createdAt: serverTimestamp(),
        }
      );
      await fetchPromoProfiles();
      setShowPromotionalModal(false);
      Swal.fire("Éxito", "Perfil promocional creado correctamente.", "success");
    } catch (err) {
      console.error("Error creando perfil promocional:", err);
      Swal.fire("Error", "No se pudo crear el perfil promocional.", "error");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = "/login";
    } catch (err) {
      console.error("Error al cerrar sesión:", err);
      Swal.fire("Error", "No se pudo cerrar la sesión.", "error");
    }
  };

  // ── Bloqueo total para cuentas rechazadas ────────────────────────────────
  if (currentUser?.status === "rejected") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-red-700 px-6 py-5 flex flex-col items-center gap-2">
            <span className="text-5xl">🚫</span>
            <h2 className="text-xl font-extrabold text-white text-center">Cuenta no autorizada</h2>
          </div>
          <div className="px-6 py-6 space-y-4 text-sm text-gray-700 dark:text-gray-300 text-center">
            <p className="text-base font-semibold text-gray-800 dark:text-white">
              Tu solicitud de acceso a <span className="text-red-500 font-bold">SIT</span> fue rechazada.
            </p>
            <p>
              Esto puede deberse a que la información proporcionada durante el registro no pudo ser validada por nuestro equipo de moderación.
            </p>
            <p>
              Si crees que esto es un error o deseas que revisemos tu caso, comunícate con soporte:
            </p>
            <a
              href="mailto:soporte@sit-app.com"
              className="inline-block bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-semibold px-4 py-2 rounded-xl border border-red-200 dark:border-red-700 hover:bg-red-100 transition"
            >
              📧 soporte@sit-app.com
            </a>
          </div>
          <div className="px-6 pb-6">
            <button
              onClick={handleLogout}
              className="w-full bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white font-bold py-3 rounded-xl transition shadow-md"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 bg-gray-100 dark:bg-gray-900 dark:text-white">
      <div className="max-w-full sm:max-w-4xl mx-auto bg-white dark:bg-gray-800 p-4 sm:p-8 rounded-2xl shadow-lg">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white">Área Promocional</h1>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button onClick={() => setIsModalOpen(true)}>
              <img
                src={
                  currentUser?.photoURL ||
                  `https://ui-avatars.com/api/?name=${
                    currentUser?.displayName || "User"
                  }`
                }
                alt="Avatar"
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-gray-200 dark:border-gray-600"
              />
            </button>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="px-3 py-2 text-sm font-medium rounded-lg bg-gray-200 dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {darkMode ? "☀️ Claro" : "🌙 Oscuro"}
            </button>
           
          </div>
        </div>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-6 sm:mb-8">
          Bienvenido {currentUser?.displayName || currentUser?.email}. Desde aquí
          administras tus perfiles y publicaciones promocionales.
        </p>

        {/* Barra de notificaciones y nuevos íconos */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row justify-end items-center gap-4">
          <NotificationHistoryProm />
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowAutoridades(true)}
              className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              title="Autoridades del Sistema"
            >
              <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <button
              onClick={() => setShowAcercaDe(true)}
              className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              title="Acerca de"
            >
              <InformationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        {/* Sección: Perfil Promocional */}
        <div className="border rounded-2xl p-4 sm:p-6 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center mb-4">
            <BriefcaseIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Perfil Promocional</h2>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {loadingProfiles ? (
              <p className="text-gray-400 text-sm sm:text-base">Cargando perfiles...</p>
            ) : hasPromoProfiles ? (
              <>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                  Gestiona tus perfiles y publicaciones promocionales activas
                  desde aquí. Mantén tu presencia siempre actualizada.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <button
                    onClick={() => setShowManagePromotionalPosts(true)}
                    className="px-4 py-2 sm:py-3 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors duration-200 text-sm sm:text-base"
                  >
                    Administrar Publicaciones
                  </button>

                  <button
                    onClick={() => setShowProfileEditor(true)}
                    className="px-4 py-2 sm:py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-colors duration-200 text-sm sm:text-base"
                  >
                    Editar Perfiles
                  </button>

                  <button
                    onClick={() => {
                      setShowRenewModal(true);
                      setShowPromotionalModal(false);
                      setShowProfileEditor(false);
                    }}
                    className="px-4 py-2 sm:py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
                  >
                    Renovar Membresía
                  </button>
                </div>

                {/* Lista de perfiles */}
                <div className="mt-4 sm:mt-6">
                  <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Tus perfiles:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {promoProfiles.map((p) => (
                      <div
                        key={p.id}
                        className="p-3 sm:p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">{p.name}</div>
                            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                              {Array.isArray(p.tags) ? p.tags.join(", ") : ""}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400">
                            {p.createdAt ? "Registrado" : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                  Crea tu perfil promocional para destacar tus proyectos o
                  emprendimientos. ¡Es la mejor forma de atraer atención!
                </p>

                <button
                  onClick={() => setShowPromotionalModal(true)}
                  className="px-4 py-2 sm:py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
                >
                  Crear mi perfil promocional
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modales / overlays */}
      {showManagePromotionalPosts && (
        <ManagePromotionalPosts
          currentUser={currentUser}
          onClose={() => setShowManagePromotionalPosts(false)}
        />
      )}

      {showProfileEditor && (
        <EditarPerfilesPromocionales
          isOpen={showProfileEditor}
          userId={currentUser?.uid}
          currentUser={currentUser}
          onClose={() => {
            setShowProfileEditor(false);
            fetchPromoProfiles();
          }}
        />
      )}

      {showPromotionalModal && (
        <PromotionalProfileModal
          isOpen={showPromotionalModal}
          onClose={() => setShowPromotionalModal(false)}
          userId={currentUser?.uid}
        />
      )}

      {showRenewModal && (
        <RenovarMembresia
          isOpen={showRenewModal}
          onClose={() => setShowRenewModal(false)}
        />
      )}

      {showAutoridades && (
        <AutoridadesSistema
          isOpen={showAutoridades}
          onClose={() => setShowAutoridades(false)}
        />
      )}

      {showAcercaDe && (
        <AcercaDeSit
          isOpen={showAcercaDe}
          onClose={() => setShowAcercaDe(false)}
        />
      )}

      {selectedAlert && (
             <AlertModal
          alert={selectedAlert}
          userRole={currentUser?.role} // <-- pasa el rol del usuario
          onClose={handleCloseAlert}
        />
      )}

      <ProfileModalProm isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}